import Swal from "sweetalert2";
import CustomAlert from "./CustomAlert";
import { baseUrl } from "./apiList";
import axios from "axios";
import {
  incrementActiveRequests,
  decrementActiveRequests,
} from "../context/LoaderContext";

const validateResponse = (apiData) => {
  if (typeof apiData == "undefined") {
    return {
      status: false,
      error: "unauthorized",
    };
  }
  return apiData;
};

const API = axios.create({
  baseURL: baseUrl,
});

API.interceptors.request.use((config, response) => {
  const token = sessionStorage.getItem("token");
  if (token) config.headers["Authtoken"] = token;

  return config;
});

// The backend rotates the auth token on almost every response, so overlapping
// requests can race: request B can be sent with a token that request A's
// still-pending response is about to invalidate. This queue serializes all
// calls made through the `API` instance so each one's rotated token is stored
// before the next one's request interceptor reads it. It also drives the
// global loader counter so the loader stays visible for the whole queued
// batch, not just a single request.
// NOTE: postApiDataNew/postfileData/updateApiData use raw `fetch`, not this
// `API` instance, so they don't share this race and aren't queued here.
let requestQueue = Promise.resolve();

function enqueue(task) {
  incrementActiveRequests();
  const run = () => task().finally(decrementActiveRequests);
  const result = requestQueue.then(run, run);
  requestQueue = result.then(
    () => undefined,
    () => undefined
  );
  return result;
}

export function axiosPostApi(url, payload, headers = {}) {
  return enqueue(async () => {
    try {
      const response = await API.post(url, payload, {
        headers: {
          // "Content-Type": "application/json",
          Accept: "application/json",
          ...headers,
        },
      });

      // Log request headers
      // console.log("Request Headers:", response.config.headers["Authtoken"]);
      // console.log("Request Headers 1:", response);
      if (response.headers.authtoken) {
        sessionStorage.setItem("token", response.headers.authtoken);
      }

      // Log response headers

      if (response.data.respCode === "IS") {
        await Swal.fire({
          title: "Oops...Session has been expired!!!",
          icon: "question",
          showDenyButton: false,
          showCancelButton: false,
          confirmButtonText: "Login Again",
          denyButtonText: `Deny`,
          allowOutsideClick: false,
        })
          .then((result) => {
            /* Read more about isConfirmed, isDenied below */
            if (result.isConfirmed) {
              sessionStorage.clear();
              sessionStorage.clear();
              window.location.href = "/";
              window.location.reload();
            } else if (result.isDenied) {
            }
          })
          .catch((err) => {
            CustomAlert("kk", "kkj");
          });
      }
      return validateResponse(response);
    } catch (error) {
      if (error.response && error.response.status == 401) {
        await Swal.fire({
          title: "Oops...Session has been expired!!!",
          icon: "question",
          showDenyButton: false,
          showCancelButton: false,
          confirmButtonText: "Login Again",
          denyButtonText: `Deny`,
          allowOutsideClick: false,
        })
          .then((result) => {
            if (result.isConfirmed) {
              sessionStorage.clear();
              window.location.href = "/";
            }
          })
          .catch((err) => {
            CustomAlert("kk", "kkj");
          });
      } else if (error.name == "AxiosError") {
        Swal.fire({
          title: "Network Error",
          icon: "error",
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            sessionStorage.clear();
            window.location.href = "/";
          }
        });
      } else {
        console.error("Error in postApiData:", error);
        throw error; // Rethrow the error for higher level handling if necessary
      }
    }
  });
}

export function axiosPostFormData(url, formDataObj, headers = {}) {
  return enqueue(async () => {
    try {
      // Prepare FormData
      const formData = new FormData();
      for (const key in formDataObj) {
        formData.append(key, formDataObj[key]);
      }

      const response = await API.post(url, formData, {
        headers: {
          Accept: "application/json",
          ...headers, // custom headers like auth token
          // DO NOT manually set 'Content-Type' for multipart/form-data
        },
      });

      // Store token if present
      if (response.headers.authtoken) {
        sessionStorage.setItem("token", response.headers.authtoken);
      }

      // Handle session expiration response code
      // if (response.data.respCode === "IS") {
      //   await Swal.fire({
      //     title: "Oops...Session has been expired!!!",
      //     icon: "question",
      //     showDenyButton: false,
      //     showCancelButton: false,
      //     confirmButtonText: "Login Again",
      //     allowOutsideClick: false,
      //   }).then((result) => {
      //     if (result.isConfirmed) {
      //       sessionStorage.clear();
      //       window.location.href = "/";
      //       window.location.reload();
      //     }
      //   }).catch((err) => {
      //     CustomAlert("Session Error", "Unable to handle session expiration.");
      //   });
      // }

      return validateResponse(response);

    } catch (error) {
      if (error.response && error.response.status === 401) {
        await Swal.fire({
          title: "Oops...Session has been expired!!!",
          icon: "question",
          showDenyButton: false,
          showCancelButton: false,
          confirmButtonText: "Login Again",
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) {
            sessionStorage.clear();
            window.location.href = "/";
          }
        }).catch(() => {
          CustomAlert("Session Error", "Unable to handle session expiration.");
        });
      } else if (error.name === "AxiosError") {
        Swal.fire({
          title: "Network Error",
          icon: "error",
          confirmButtonText: "OK",
        }).then(() => {
          sessionStorage.clear();
          window.location.href = "/";
        });
      } else {
        console.error("Error in axiosPostFormData:", error);
        throw error;
      }
    }
  });
}


export function axiosGetApi(url, headers = {}) {
  return enqueue(async () => {
    try {
      const response = await API.get(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...headers,
        },
      });

      // Store token in session if available
      if (response.headers.authtoken) {
        sessionStorage.setItem("token", response.headers.authtoken);
      }

      // Handle session expiration
      if (response.data.respCode === "IS") {
        await Swal.fire({
          title: "Oops...Session has been expired!!!",
          icon: "question",
          showDenyButton: false,
          showCancelButton: false,
          confirmButtonText: "Login Again",
          denyButtonText: `Deny`,
          allowOutsideClick: false,
        })
          .then((result) => {
            if (result.isConfirmed) {
              sessionStorage.clear();
              window.location.href = "/";
              window.location.reload();
            }
          })
          .catch((err) => {
            CustomAlert("kk", "kkj");
          });
      }

      // Return the validated response
      return validateResponse(response);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        await Swal.fire({
          title: "Oops...Session has been expired!!!",
          icon: "question",
          showDenyButton: false,
          showCancelButton: false,
          confirmButtonText: "Login Again",
          allowOutsideClick: false,
        })
          .then((result) => {
            if (result.isConfirmed) {
              sessionStorage.clear();
              window.location.href = "/";
            }
          })
          .catch((err) => {
            CustomAlert("kk", "kkj");
          });
      } else if (error.name === "AxiosError") {
        Swal.fire({
          title: "Network Error",
          icon: "error",
        });
      } else {
        console.error("Error in axiosGetApi:", error);
        throw error;
      }
    }
  });
}

export function getApiDataPdf(url, headers) {
  return enqueue(async () => {
    try {
      //const response = await API.get(url,headers);
      const response = await API.get(url, { headers });
      if (response.headers.authtoken) {
        sessionStorage.setItem("token", response.headers?.authtoken);
      }

      if (response.data.respCode === "IS") {
        await Swal.fire({
          title: "Oops...Session has been expired!!!",
          icon: "question",
          showDenyButton: false,
          showCancelButton: false,
          confirmButtonText: "Login Again",
          denyButtonText: `Deny`,
          allowOutsideClick: false,
        })
          .then((result) => {
            /* Read more about isConfirmed, isDenied below */
            if (result.isConfirmed) {
              sessionStorage.clear();
              sessionStorage.clear();
              window.location.href = "/";
              window.location.reload();
            } else if (result.isDenied) {
            }
          })
          .catch((err) => {
            CustomAlert("kk", "kkj");
          });
      }
      return validateResponse(response);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        await Swal.fire({
          title: "Oops...Session has been expired!!!",
          icon: "question",
          showDenyButton: false,
          showCancelButton: false,
          confirmButtonText: "Login Again",
          denyButtonText: `Deny`,
          allowOutsideClick: false,
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            sessionStorage.clear();
            sessionStorage.clear();
            window.location.href = "/";
          } else if (result.isDenied) {
          }
        });
        // window.location.href = "/vakrangeeatmadminportal"
      } else {
        // Handle other errors
        console.error("Error in axiosGetApiData:", error);
        throw error; // Rethrow the error for higher level handling if necessary
      }
    }
  });
}

export async function postApiDataNew(url, payload) {
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: "Bearer " + sessionStorage.getItem("jwtToken"),
    },
  });
  if (response.headers.authorization) {
    sessionStorage.setItem("token", response.headers?.authorization);
  }
  const apiData = await response.json();
  return validateResponse(apiData);
}

export async function postfileData(url, file, headers) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(url, {
    // mode: 'no-cors',
    method: "POST",
    body: formData,
    // body: JSON.stringify(payload),
    // redirect: 'follow',
    headers: {
      "Content-Type": "multipart/form-data",
      Accept: "/",
      type: "formData",
      ...headers,
    },
  });
  const apiData = await response.json();
  return validateResponse(apiData);
}

export async function updateApiData(url, payload) {
  const response = await fetch(url, {
    method: "PUT",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  const apiData = await response.json();
  return validateResponse(apiData);
}

export function axiosPutApi(url, payload, headers = {}) {
  return enqueue(async () => {
    try {
      const response = await API.put(url, payload, {
        headers: {
          Accept: "application/json",
          ...headers,
        },
      });
      if (response.headers.authtoken) {
        sessionStorage.setItem("token", response.headers.authtoken);
      }
      return validateResponse(response);
    } catch (error) {
      if (error.name === "AxiosError") {
        Swal.fire({
          title: "Network Error",
          icon: "error",
        });
      }
      throw error;
    }
  });
}

export function axiosDeleteApi(url, headers = {}) {
  return enqueue(async () => {
    try {
      const response = await API.delete(url, {
        headers: {
          Accept: "application/json",
          ...headers,
        },
      });
      if (response.headers.authtoken) {
        sessionStorage.setItem("token", response.headers.authtoken);
      }
      return validateResponse(response);
    } catch (error) {
      if (error.name === "AxiosError") {
        Swal.fire({
          title: "Network Error",
          icon: "error",
        });
      }
      throw error;
    }
  });
}

// Returns the response for every HTTP status, so callers can read the backend's
// 4xx/5xx JSON. Only a dead session (401, or 403 "Invalid Token") logs out.
export function axiosRequest(method, url, payload) {
  return enqueue(async () => {
    let response;
    try {
      response = await API.request({
        method,
        url,
        data: payload,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        validateStatus: () => true,
      });
    } catch (error) {
      return { status: 0, data: { error: "Network error, please check your connection" } };
    }

    if (response.headers.authtoken) {
      sessionStorage.setItem("token", response.headers.authtoken);
    }

    const sessionDead =
      response.status === 401 ||
      (response.status === 403 && response.data?.respCode === "403");

    if (sessionDead) {
      await Swal.fire({
        title: "Oops...Session has been expired!!!",
        icon: "question",
        confirmButtonText: "Login Again",
        allowOutsideClick: false,
      });
      sessionStorage.clear();
      window.location.href = "/";
    }
    return response;
  });
}
